import type { ComboboxOption } from '@opendoor/atoms/combobox';
import { PropertyType } from '__generated__/athena/graphql';

import type { GeoPoint } from '../map/types';
import type { RangeOption } from './types';

/**
 * Build the option list for a range-filter `Combobox`. The placeholder option
 * (e.g. "No min"/"No max") is prepended as a selectable row so users can reset
 * the filter, unless an option with the same value already exists (e.g. the
 * price filter's "$2,000,000+" shares the "No max" value) — guarding against
 * duplicate values, which the Combobox keys by.
 */
export const buildRangeFilterOptions = (
  options: RangeOption[],
  placeholderOption: RangeOption,
): ComboboxOption[] =>
  options.some((option) => option.value === placeholderOption.value)
    ? options
    : [placeholderOption, ...options];

/**
 * For use with Dropdown values and options to prevent invalid states in filter ranges
 *
 * @param options a list of RangeOptions for a Dropdown
 * @param op whether options should be accepted above or below the limit
 * @param limit a stringified integer representing the limit
 * @returns the filtered list of RangeOptionsZ
 */
export const filterIntOptions = (
  options: RangeOption[],
  op: 'gt' | 'lt',
  limit?: string,
): RangeOption[] => {
  if (!limit) {
    return options.slice();
  }

  const limitVal = parseInt(limit, 10);
  if (isNaN(limitVal)) {
    return options.slice();
  }

  return options.filter((option) => {
    const val = parseInt(option.value, 10);
    if (op === 'gt') {
      return val > limitVal;
    }

    return val < limitVal;
  });
};

enum MapSearchZoom {
  CITY = 9,
  ZIP = 11,
  NEIGHBORHOOD = 13,
}

// Based on API limits
export const MAX_MARKETS = 150;

// Fallback market when none specified or invalid
export const DEFAULT_MARKET = 'PHOENIX';

export const getZoomForLocationType = (zoneType?: string): number => {
  if (zoneType === '(Zip code)') {
    return MapSearchZoom.ZIP;
  }
  if (zoneType === '(Neighborhood)') {
    return MapSearchZoom.NEIGHBORHOOD;
  }
  return MapSearchZoom.CITY;
};

/**
 * Convert GeoJSON polygon coordinates to GeoPoint format
 */
export const convertToGeoPointVertices = (vertices: number[][][]): GeoPoint[][] => {
  return vertices?.map((polygon) => polygon.map(([lon, lat]) => ({ lon, lat }))) ?? [];
};

export const buildPropertyTypes = (
  singleFamily?: boolean | null,
  condo?: boolean | null,
  townhome?: boolean | null,
): PropertyType[] | undefined => {
  const propertyTypes: PropertyType[] = [];

  if (singleFamily) {
    propertyTypes.push(PropertyType.HOUSE);
  }
  if (condo) {
    propertyTypes.push(PropertyType.CONDO);
  }
  if (townhome) {
    propertyTypes.push(PropertyType.TOWNHOUSE);
  }

  // Together, Cosmos's three visible choices mean "all homes," including
  // indexed dwelling types the UI does not expose as individual filters.
  return propertyTypes.length === 1 || propertyTypes.length === 2 ? propertyTypes : undefined;
};
